import { PrismicRichText } from '@prismicio/react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import LeftIllustration from '../assets/work-benefit-left.svg';
import RightIllustration from '../assets/work-benefit-right.svg';

import { Section as SectionComponent, Label, Title } from './BenefitsOverviewSlice';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, desktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const Section = styled(SectionComponent)`
  && {
    ${fluidProp(['padding-top', 'padding-bottom'], '80px', '120px')}
    overflow: hidden;

    ${ContentContainer} {
      z-index: 1;
      position: relative;
    }
  }
`;

const BenefitBlock = styled.div`
  width: 45%;
  margin-bottom: 60px;

  ${tabletBreakpoint(css`
    width: 90%;
  `)}
`;

const BenefitsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 40px;
`;

export const BlockTitle = styled.div`
  ${({ theme }) => theme.typography.titleLG}
  font-weight: 700;
  margin-top: 20px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const Index = styled.div`
  ${({ theme }) => theme.typography.titleLG}
  font-weight: 700;
  color: var(--primaryColor);
  font-family: var(--typography-fontBossa);
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.body}
  color: ${({ theme }) => theme.colors.darkBlue_75};
`;

const SecondParagraph = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 21px;
  align-items: center;

  ${tabletBreakpoint(css`
    gap: 10px;
  `)}
`;

export const Percentage = styled.div`
  ${({ theme }) => theme.typography.scale3}
  color:  var(--primaryColor);
  font-family: var(--typography-fontBossa);

  > * {
    margin: 0;
  }
`;

const LeftImageWrapper = styled.div`
  position: absolute;
  top: 10%;
  left: 0;
  z-index: -1;

  ${desktopBreakpoint(css`
    display: none;
  `)}
`;

const RightImageWrapper = styled(LeftImageWrapper)`
  left: unset;
  top: unset;
  right: 0;
  bottom: 10%;
`;

export function WorkBenefitSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { label, title } = primary;

  return (
    <Section>
      <ContentContainer>
        {fieldHasContent(label) && <Label>{label}</Label>}
        {fieldHasContent(title) && (
          <Title>
            <PrismicRichText field={title} />
          </Title>
        )}
        <BenefitsWrapper>
          {fields.map(
            (
              { benefitTitle, firstParagraph, secondPercentage, secondParagraph, thirdParagraph },
              index
            ) => (
              <BenefitBlock key={index}>
                <Index>0{index + 1}</Index>
                {fieldHasContent(benefitTitle) && (
                  <BlockTitle>
                    <PrismicRichText field={benefitTitle} />
                  </BlockTitle>
                )}
                {fieldHasContent(firstParagraph) && (
                  <Description>
                    <PrismicRichText field={firstParagraph} />
                  </Description>
                )}
                <SecondParagraph>
                  {fieldHasContent(secondPercentage) && (
                    <Percentage>
                      <PrismicRichText field={secondPercentage} />
                    </Percentage>
                  )}
                  {fieldHasContent(secondParagraph) && (
                    <Description>
                      <PrismicRichText field={secondParagraph} />
                    </Description>
                  )}
                </SecondParagraph>
                {fieldHasContent(thirdParagraph) && (
                  <Description>
                    <PrismicRichText field={thirdParagraph} />
                  </Description>
                )}
              </BenefitBlock>
            )
          )}
        </BenefitsWrapper>
      </ContentContainer>
      <LeftImageWrapper>
        <LeftIllustration />
      </LeftImageWrapper>
      <RightImageWrapper>
        <RightIllustration />
      </RightImageWrapper>
    </Section>
  );
}
